import { jsx } from 'react/jsx-runtime';
import { forwardRef, useId, useRef } from 'react';
import clsx from 'clsx';
import { useDetectTruncated } from '../hooks/use-detect-truncated.js';
import { Label } from '../label/label.js';
import { Root as Root$1, Option as Option$1, ManuallyLabeledOption } from '../radio-like-primitive/radio-like-primitive.js';
import { fieldset, size, optionRoot, icon, text, textContent, optionRootTruncated } from './segmented-control.css.js';

/**
 * Root element for SegmentedControl, which uses icons to choose between a set of independent states.
 *
 * @compose { "root": true, "strictChildren": true }
 * @see https://fpl.figma.design/components/segmented-control/
 */
const Root = forwardRef(({ children, size: size$1 = 'md', ...props }, ref) => (jsx(Root$1, { "data-fpl-component": "", ...props, className: clsx(fieldset, size[size$1]), ref: ref, children: children })));
Root.displayName = 'SegmentedControl.Root';
/**
 * An option of a segmented control, nested within a .Root
 *
 * @note Option and TextOption children should not be mixed.
 * @compose { "parent": ["SegmentedControl.Root"], "multi": true }
 */
const Option = forwardRef(({ icon: icon$1, ...props }, ref) => (jsx(Option$1, { ...props, ref: ref, className: optionRoot, children: jsx("span", { className: icon, "aria-hidden": true, children: icon$1 }) })));
Option.displayName = 'SegmentedControl.Option';
/**
 * An option of a segmented control that uses text instead of an icon.
 * To be nested within a SegmentedControl.Root parent.
 *
 * @note Option and TextOption children should not be mixed.
 * @compose { "parent": ["SegmentedControl.Root"], "multi": true }
 */
const TextOption = forwardRef(({ label, truncate, ...props }, ref) => {
    const id = useId();
    const labelRef = useRef(null);
    const isTruncated = useDetectTruncated(labelRef);
    const tooltipProps = truncate && isTruncated ? { 'data-tooltip': label, 'data-tooltip-type': 'text' } : null;
    return (jsx(ManuallyLabeledOption, { ...props, ref: ref, id: id, className: clsx(optionRoot, { [optionRootTruncated]: truncate }), children: jsx(Label, { ref: labelRef, htmlFor: id, className: text, ...(truncate && tooltipProps), children: jsx("span", { className: textContent, children: label }) }) }));
});
TextOption.displayName = 'SegmentedControl.TextOption';

export { Option, Root, TextOption };
//# sourceMappingURL=segmented-control.js.map
